import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Check, FileDown } from "lucide-react";
import { SignaturePad } from "@/components/SignaturePad";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FIELD = "w-full bg-white border border-[#E3DECE] px-4 py-3 text-sm focus:outline-none focus:border-[#BA6D52] transition-colors";
const LABEL = "block text-[10px] uppercase tracking-[0.18em] text-[#757067] mb-2";

const fmt = (d) => (d ? new Date(d).toLocaleDateString("de-DE") : "—");

export default function VertragUnterschreiben() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", ort: "" });
  const [signature, setSignature] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/vermittlung/vertrag/unterschreiben/${id}?token=${token}`);
      setData(r.data);
      setForm((f) => ({ ...f, name: f.name || r.data.ansprechpartner || "", ort: f.ort || r.data.ort || "" }));
    } catch (err) {
      setError(err.response?.data?.detail || "Dieser Link ist nicht mehr gültig.");
    }
  }, [id, token]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Bitte Ihren vollständigen Namen angeben");
    if (!signature) return toast.error("Bitte im Feld unterschreiben");
    setBusy(true);
    try {
      await axios.post(`${API}/vermittlung/vertrag/unterschreiben/${id}?token=${token}`,
        { ...form, signature_data_url: signature });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Unterschrift konnte nicht gespeichert werden");
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <div className="max-w-xl mx-auto px-6 py-24 md:py-32" data-testid="sign-error">
        <h1 className="font-serif text-3xl font-light tracking-tight">Link nicht gültig</h1>
        <p className="text-[#575349] mt-5 leading-relaxed">{error}</p>
        <p className="text-sm text-[#757067] mt-5">Melden Sie sich gerne direkt: +49 151 27282560</p>
      </div>
    );
  }
  if (!data) return <div className="max-w-xl mx-auto px-6 py-32 text-sm text-[#757067]">Lade …</div>;

  if (done || data.already_signed) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 md:py-32" data-testid="sign-success">
        <Check className="w-10 h-10 text-[#BA6D52]" strokeWidth={1.4} />
        <h1 className="font-serif text-4xl sm:text-5xl font-light tracking-tighter mt-8">Unterschrieben.</h1>
        <p className="text-[#575349] mt-6 leading-relaxed">
          Vielen Dank — die Provisionsvereinbarung zum Vorgang <b className="text-[#2C2A26]">{data.request_number}</b> ist
          bei mir eingegangen. Das unterschriebene PDF ist gerade per E-Mail zu Ihnen unterwegs.
          Ich bestätige Ihnen den Einsatz jetzt verbindlich und melde mich mit allen Details.
        </p>
      </div>
    );
  }

  const isTemp = data.placement_type === "temporary";
  const gross = (data.provision_total_eur * (1 + data.mwst_percent / 100)).toFixed(2);

  return (
    <div className="max-w-2xl mx-auto px-6 md:px-12 py-16 md:py-24" data-testid="sign-page">
      <p className="text-[10px] uppercase tracking-[0.35em] text-[#BA6D52]">Provisionsvereinbarung</p>
      <h1 className="font-serif text-4xl sm:text-5xl font-light tracking-tighter mt-6">
        Einmal unterschreiben,<br />dann ist alles geregelt.
      </h1>
      <p className="text-[#757067] font-light text-lg mt-6 leading-relaxed">
        Guten Tag {data.ansprechpartner}, hier können Sie die Vereinbarung direkt im Browser unterschreiben —
        kein Drucken, kein Scannen. Danach bestätige ich Ihnen den Einsatz verbindlich.
      </p>

      <div className="mt-10 bg-[#F2EFE9] p-6 md:p-8" data-testid="sign-summary">
        <p className="text-[10px] uppercase tracking-[0.25em] text-[#BA6D52]">Vorgang {data.request_number}</p>
        <div className="mt-4 space-y-2 text-sm">
          {[["Betrieb", data.betrieb],
            ["Anschrift", `${data.strasse ? data.strasse + ", " : ""}${data.plz} ${data.ort}`],
            isTemp ? ["Einsatzzeitraum", `${fmt(data.von_datum)} – ${fmt(data.bis_datum)}`]
                   : ["Eintritt ab", fmt(data.eintritt_ab)],
            isTemp ? ["Einsatztage", `${data.einsatztage} Tage · ${data.anzahl_koeche} Koch/Köche`]
                   : ["Art", "Vermittlung in Festanstellung"],
            ["Provision", isTemp
              ? `${data.provision_rate_eur.toFixed(2)} € netto je Koch und Einsatztag`
              : `${data.provision_fee_eur.toFixed(2)} € netto einmalig`],
          ].map(([k, v]) => (
            <div key={k} className="flex gap-4">
              <span className="text-[#757067] min-w-[130px] shrink-0">{k}</span>
              <span className="text-[#2C2A26]">{v}</span>
            </div>
          ))}
        </div>
        <div className="mt-5 pt-5 border-t border-[#E3DECE] flex items-baseline justify-between">
          <span className="text-[10px] uppercase tracking-[0.2em] text-[#757067]">Voraussichtlich</span>
          <span className="font-serif text-2xl">{data.provision_total_eur.toFixed(2)} € netto</span>
        </div>
        <p className="text-xs text-[#757067] mt-2">
          entspricht {gross} € inkl. {data.mwst_percent} % MwSt. Abgerechnet werden ausschließlich
          tatsächlich geleistete Einsatztage. Die Leistung des Kochs rechnet dieser direkt mit Ihnen ab.
        </p>
      </div>

      <a href={`${API}/vermittlung/vertrag/unterschreiben/${id}/pdf?token=${token}`} target="_blank" rel="noreferrer"
        data-testid="sign-contract-download"
        className="inline-flex items-center gap-2 mt-6 border border-[#BA6D52] text-[#BA6D52] hover:bg-[#FAF5F1] px-5 py-3 text-[11px] uppercase tracking-[0.2em] transition-colors">
        <FileDown className="w-4 h-4" /> Vereinbarung vorher lesen
      </a>

      <form onSubmit={submit} className="mt-12 space-y-6">
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <label className={LABEL}>Vollständiger Name *</label>
            <input className={FIELD} required value={form.name} data-testid="sign-name"
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          </div>
          <div>
            <label className={LABEL}>Ort</label>
            <input className={FIELD} value={form.ort} data-testid="sign-ort"
              onChange={(e) => setForm((f) => ({ ...f, ort: e.target.value }))} />
          </div>
        </div>
        <div className="bg-[#F2EFE9] p-6">
          <p className={LABEL}>Ihre Unterschrift *</p>
          <SignaturePad onChange={setSignature} testId="betrieb-signature" />
        </div>
        <p className="text-xs text-[#757067] leading-relaxed">
          Mit dem Absenden erklären Sie sich mit der Provisionsvereinbarung einverstanden. Datum, Uhrzeit und
          eine Dokumentenkennung werden zum Nachweis gespeichert. Sie erhalten das unterschriebene PDF
          unmittelbar per E-Mail.
        </p>
        <button type="submit" disabled={busy} data-testid="sign-submit"
          className="w-full sm:w-auto bg-[#BA6D52] text-white hover:bg-[#9E5841] disabled:opacity-60 px-10 py-4 text-[11px] uppercase tracking-[0.25em] transition-colors">
          {busy ? "Wird gespeichert …" : "Rechtsverbindlich unterschreiben"}
        </button>
      </form>
    </div>
  );
}
